/**
 * The Member Support booking calendar the Book a call page embeds: one shared
 * Healthie scheduling link, set per environment at build time.
 *
 * The frame URL carries the member's name, email and phone to Healthie in its
 * query string, which is how Healthie pre-fills its form. That is the only
 * place those values may go: nothing here logs, tracks or stores them.
 */

import { signupBookCallCalendarUrlSetting } from '@/services/build-environment.mjs';

/** The only hosts a configured link may point at, so a mistyped value never sends member details elsewhere. */
const HEALTHIE_EMBED_HOSTS: ReadonlySet<string> = new Set([
  'secure.gethealthie.com',
  'securestaging.gethealthie.com',
]);

const HEALTHIE_EMBED_PATH = '/appointments/embed_appt';

/** The message Healthie's embed posts to its parent once a booking is made. */
const HEALTHIE_BOOKING_COMPLETED = 'healthie.booking_process_completed';

/**
 * The configured calendar link, or `null` when it is unset or is not a
 * Healthie embed link. Never throws. The default is the link built into the
 * bundle.
 */
export function readBookCallCalendarUrl(
  raw: string | undefined = signupBookCallCalendarUrlSetting(),
): URL | null {
  if (!raw) return null;
  let url: URL;
  try {
    url = new URL(raw.trim());
  } catch {
    return null;
  }
  const isEmbed =
    url.protocol === 'https:' &&
    HEALTHIE_EMBED_HOSTS.has(url.hostname) &&
    url.port === '' &&
    url.username === '' &&
    url.password === '' &&
    url.pathname === HEALTHIE_EMBED_PATH;
  return isEmbed ? url : null;
}

/** What the flow knows about the member, any of which may be missing. */
export interface BookCallPrefill {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
}

const PREFILL_PARAMS: ReadonlyArray<readonly [keyof BookCallPrefill, string]> = [
  ['firstName', 'first_name'],
  ['lastName', 'last_name'],
  ['email', 'email'],
  ['phone', 'phone_number'],
];

/**
 * The frame URL: the configured link with its own query kept, plus each
 * known, non-blank value under the name Healthie reads.
 */
export function buildBookCallFrameUrl(base: URL, prefill: BookCallPrefill): string {
  const url = new URL(base.href);
  for (const [field, param] of PREFILL_PARAMS) {
    const value = prefill[field]?.trim();
    if (value) url.searchParams.set(param, value);
  }
  return url.href;
}

/** The embedded frame a completed-booking message must come from. */
export interface BookCallFrame {
  origin: string;
  window: Window | null;
}

/**
 * Whether a `message` event is Healthie reporting a completed booking from
 * the embedded frame itself: the frame's own window, the configured origin,
 * and the completed-booking event name. Anything else is ignored.
 */
export function isHealthieBookingCompleted(event: MessageEvent, frame: BookCallFrame): boolean {
  if (frame.window === null || event.source !== frame.window) return false;
  if (event.origin !== frame.origin) return false;
  const data: unknown = event.data;
  return (
    typeof data === 'object' &&
    data !== null &&
    'event' in data &&
    data.event === HEALTHIE_BOOKING_COMPLETED
  );
}
